import {
  injectSlice,
  createAsyncThunk,
  createSelector,
  createSlice,
  createAction,
} from '@bonanza-social/toolkit/state';
import { ItemsState, ShopItemPurchaseResponseType } from './items.types';
import ShopService from '../services/ShopService/ShopService';
import { getDateTimeStamp } from '@bonanza-social/toolkit/utils';
import { ShopRouteParamsType } from '../types';
import { cloneDeep } from 'lodash-es';
import { ApiError } from '@bonanza-social/toolkit/api';
import { ShopItemPurchaseResponseSchema } from '../services/ShopService/ShopService.schemas';
import { ZodError } from '@bonanza-social/toolkit/api';

export const openShopRewardsBottomSheet =
  createAction<ShopItemPurchaseResponseType>('shopItems/showRewards');

export const purchaseShopItem = createAsyncThunk(
  'shopItems/purchase',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await ShopService.purchaseItem({
        pathParams: { id },
      });

      ShopItemPurchaseResponseSchema.parse(response);

      return response;
    } catch (e) {
      if (e instanceof ApiError || e instanceof ZodError) {
        return rejectWithValue(e);
      }

      throw e;
    }
  },
);

export const fetchShopItems = createAsyncThunk(
  'shopItems/fetch',
  async () => await ShopService.getItems(),
);

const initialState: ItemsState = {
  dataState: 'idle',
  data: [],
};

const items = createSlice({
  name: 'items',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchShopItems.pending, (state) => {
      state.dataState =
        state.dataState === 'fulfilled' ? 'refreshing' : 'pending';
    });

    builder.addCase(fetchShopItems.rejected, (state) => {
      state.dataState = 'rejected';
    });

    builder.addCase(fetchShopItems.fulfilled, (state, action) => {
      state.dataState = 'fulfilled';
      state.data = action.payload.items;
    });
  },
});

export const withItems = injectSlice({
  reducerPath: 'items',
  reducer: items.reducer,
});

export const selectAllItems = withItems.selector((state) => state.items.data);

export const selectBestValueItems = createSelector([selectAllItems], (items) =>
  items.filter((item) => item.isBestValue),
);

export const selectCashRewardItems = createSelector([selectAllItems], (items) =>
  items.filter((item) => item.type === 'CashReward'),
);

export const selectedFreeSpinsItems = createSelector(
  [selectAllItems],
  (items) => items.filter((item) => item.type === 'FreeSpins'),
);

export const selectPhysicalItems = createSelector([selectAllItems], (items) =>
  items.filter((item) => item.type === 'Physical'),
);

export const selectMysteryBoxItems = createSelector([selectAllItems], (items) =>
  items.filter((item) => item.type === 'MysteryBox'),
);

export const selectWheelItems = createSelector([selectAllItems], (items) =>
  items.filter((item) => item.type === 'Wheel'),
);

export const selectScratcherItems = createSelector([selectAllItems], (items) =>
  items.filter((item) => item.type === 'Scratcher'),
);

export const selectPurchasedItems = createSelector([selectAllItems], (items) =>
  items
    .filter((item) => item.lastPurchasedAt)
    .sort(
      (a, b) =>
        getDateTimeStamp(b.lastPurchasedAt as string) -
        getDateTimeStamp(a.lastPurchasedAt as string),
    ),
);

export const selectItems = createSelector(
  [
    selectAllItems,
    selectPurchasedItems,
    selectBestValueItems,
    (_, options: ShopRouteParamsType) => options,
  ],
  (allItems, purchasedItems, bestValueItems, options) => {
    const { filter, sort } = options;

    let items;

    switch (filter) {
      case 'Purchased':
        items = purchasedItems;
        break;
      case 'BestValue':
        items = bestValueItems;
        break;
      default:
        items = allItems;
        break;
    }

    const sortedItems = cloneDeep(items);
    if (sort === 'HighToLow') {
      sortedItems.sort((a, b) => b.price - a.price);
    } else if (sort === 'LowToHigh') {
      sortedItems.sort((a, b) => a.price - b.price);
    }

    if (filter && filter !== 'Purchased' && filter !== 'BestValue') {
      const filteredItems = cloneDeep(sortedItems);
      return filteredItems.filter((item) => {
        const valueToCompare =
          typeof filter === 'number' ? item.sale?.id : item.type;

        return valueToCompare === filter;
      });
    }

    return sortedItems;
  },
);

export const selectItemsDataState = withItems.selector(
  (state) => state.items.dataState,
);

declare global {
  interface LazyLoadedSlices {
    items: ReturnType<typeof items.reducer>;
  }
}
